<script setup lang="ts">
import { computed } from 'vue'
import { ExternalLink, TriangleAlert } from 'lucide-vue-next'
import Button from '@/components/ui/button/Button.vue'
import Dialog from '@/components/ui/dialog/Dialog.vue'
import DialogContent from '@/components/ui/dialog/DialogContent.vue'
import DialogHeader from '@/components/ui/dialog/DialogHeader.vue'
import DialogTitle from '@/components/ui/dialog/DialogTitle.vue'
import DialogDescription from '@/components/ui/dialog/DialogDescription.vue'
import DialogFooter from '@/components/ui/dialog/DialogFooter.vue'
import type { Citation } from './useResearchChat'
import { extractActualUrl } from './citationUtils'
import { useTranslateKey } from '@/lib/utils'

const props = defineProps<{
  citation: Citation | null
}>()

const open = defineModel<boolean>('open', { required: true })
const { translateKey } = useTranslateKey()

const actualUrl = computed(() => {
  const url = props.citation?.url
  if (!url) return ''
  return extractActualUrl(url)
})

const displayTitle = computed(() => {
  return (
    props.citation?.title || actualUrl.value || translateKey('externalNavigation.sourceFallback')
  )
})

const displayUrl = computed(() => {
  const sourceUrl = actualUrl.value || props.citation?.url || ''
  if (!sourceUrl) return ''

  try {
    const parsed = new URL(sourceUrl)
    const pathname = parsed.pathname === '/' ? '' : parsed.pathname
    const compactPath = pathname.length > 42 ? `${pathname.slice(0, 39)}...` : pathname
    return `${parsed.hostname}${compactPath}`
  } catch {
    return sourceUrl.length > 80 ? `${sourceUrl.slice(0, 77)}...` : sourceUrl
  }
})

const openExternal = () => {
  if (!actualUrl.value) return
  window.open(actualUrl.value, '_blank', 'noopener,noreferrer')
  open.value = false
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="sm:max-w-lg">
      <DialogHeader class="min-w-0">
        <DialogTitle class="flex items-center gap-2">
          <TriangleAlert class="size-5 text-amber-500" />
          {{ translateKey('externalNavigation.leaveTitle') }}
        </DialogTitle>
        <DialogDescription
          class="max-w-full text-sm break-words"
          :title="actualUrl || props.citation?.url || ''"
        >
          {{ translateKey('externalNavigation.openExternalDescription') }}
        </DialogDescription>
      </DialogHeader>

      <div class="border-border rounded-md border p-3">
        <p class="text-sm font-medium">{{ displayTitle }}</p>
        <p class="text-muted-foreground mt-1 text-xs break-all">
          {{
            displayUrl ||
            actualUrl ||
            props.citation?.url ||
            translateKey('externalNavigation.unknownUrl')
          }}
        </p>
      </div>

      <DialogFooter class="w-full flex-wrap gap-2 sm:justify-end">
        <Button variant="outline" @click="open = false">{{ translateKey('cancel') }}</Button>
        <Button class="gap-2" :disabled="!actualUrl" @click="openExternal">
          <ExternalLink class="size-4" />
          {{ translateKey('externalNavigation.visitExternalUrl') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
